/**
 * Pure F3 concurrency/UX guards for the payroll wizard (PRD_PayrollPerformance F3).
 *
 * The decision logic for the staleness/recalc guards is extracted here so it can be
 * unit-tested deterministically (no React render, no timing) — the ProcessPayroll
 * handlers call these instead of inlining the conditions. Keeping them pure is what
 * lets the Verification "F3 guards" test assert the block decision and the recalc
 * re-entrancy directly. See payrollGuards.test.js.
 */

import { isIncompleteRecord } from '../../utils/dtrCompleteness';

// A DTR record that COUNTS toward the staleness snapshot / recount — a real, computable
// row: not a synthesized placeholder and not an incomplete one-sided orphan. The baseline
// (buildDtrSnapshot, over emp.timeRecords) and the live recount (checkDtrStaleness, over the
// raw Firestore doc) MUST use this same predicate, or they diverge and raise a phantom
// "records modified" banner (PRD_DtrCompletenessGuard: incompletes are stripped from
// emp.timeRecords, so the raw recount must exclude them too). Leave/absent rows derive
// complete → counted on both sides.
const isCountableDtrRecord = (r) => !r.isPlaceholder && !isIncompleteRecord(r);

// Forward-commit steps that must be blocked while the background DTR-staleness check
// is still in flight — the same transitions the existing staleEmployees gate covers,
// plus the Submit commit ('submit'). handleNext passes the nextStep; the Submit path
// passes 'submit'. Earlier transitions (→ employee-selection / → gross-income) are not
// blocked: the check only protects against advancing PAST Step 3 (or submitting) on
// gross income that may be stale.
const STALENESS_BLOCKED_TARGETS = ['deductions', 'loans', 'adjustments', 'summary', 'submit'];

/**
 * Whether a forward commit to `target` must block because a staleness check is running.
 * @param {boolean} stalenessChecking - the F3(a) in-flight flag.
 * @param {string} target - the step being advanced to, or 'submit' for the commit path.
 * @return {boolean} true ⇒ block-and-return (show the "checking…" toast, don't advance).
 */
const blocksForwardCommit = (stalenessChecking, target) =>
  Boolean(stalenessChecking) && STALENESS_BLOCKED_TARGETS.includes(target);

/**
 * A synchronous single-flight re-entrancy guard (F3(b)). Unlike a React state flag,
 * tryEnter() flips a plain in-closure boolean SYNCHRONOUSLY, so two synchronous
 * handleRecalculateGrossIncome() calls can't both pass — the second sees the guard
 * already active and returns. Held in a useRef so it persists across renders; the
 * companion `isRecalculating` state still drives the button label/disabled.
 *
 * @return {{tryEnter: function(): boolean, exit: function(): void, isActive: function(): boolean}}
 *   tryEnter() returns true and locks if free, false if already active; exit() unlocks.
 */
const makeReentrancyGuard = () => {
  let active = false;
  return {
    tryEnter() {
      if (active) return false;
      active = true;
      return true;
    },
    exit() {
      active = false;
    },
    isActive() {
      return active;
    },
  };
};

/**
 * Build the DTR staleness baseline snapshot for the in-session Summary check (OBS-4 fix).
 *
 * Maps each employee whose DETAIL is loaded (`timeRecords` is an array) to its
 * record-count / hours / OT over the period window, excluding placeholder rows
 * (which the live recount in checkDtrStaleness also drops, so the two sets match). A
 * SUMMARY-ONLY line — one whose detail never loaded, so it carries no
 * `timeRecords` at all — is OMITTED, not written as a zero baseline.
 *
 * Why omit: a `recordCount: 0` entry is indistinguishable from a real "had zero
 * records" baseline, so at the recount the employee's actual DTR (e.g. 11 records,
 * 88h) reads as "11 new records added / 0.0h → 88.0h" — a false "time records
 * modified" banner for EVERY employee (OBS-4). With no entry, checkDtrStaleness's
 * `if (!savedSnapshot) return null` correctly treats the line as "no baseline
 * yet → not stale".
 *
 * @param {Array} employees - the selected run lines at Step 2→3 (full and/or summary-only).
 * @param {string} startYMD - period start 'YYYY-MM-DD'.
 * @param {string} endYMD - period end 'YYYY-MM-DD'.
 * @return {Object} snapshot keyed by employeeId; detail-less (summary-only) lines omitted.
 */
const buildDtrSnapshot = (employees, startYMD, endYMD) => {
  const snapshot = {};
  for (const emp of employees || []) {
    if (!Array.isArray(emp.timeRecords)) continue; // summary-only line — no baseline to record
    const empId = emp.employeeId || emp.id;
    const periodRecords = emp.timeRecords.filter((r) =>
      r.date && r.date >= startYMD && r.date <= endYMD && isCountableDtrRecord(r));
    snapshot[empId] = {
      recordCount: periodRecords.length,
      totalHoursWorked: periodRecords.reduce((sum, r) => sum + (r.hoursWorked || 0), 0),
      totalOvertimeHours: periodRecords.reduce((sum, r) => sum + (r.overtimeHours || 0), 0),
    };
  }
  return snapshot;
};

/**
 * Whether the in-session Summary-step DTR-staleness check should fire on landing.
 *
 * Fires only for a FRESH in-session run (`inSession`) — a resumed draft is rebuilt from the
 * live summary on resume instead (PRD_PayrollResumeReprice C2). It is `armed`
 * at each Step-3 re-baseline and cleared after firing (so a user who goes Back, re-selects,
 * and returns to Summary is re-checked against the fresh baseline — not a one-shot latch),
 * and requires a captured in-memory baseline (`hasBaseline`). Pure so the gate + re-arm
 * logic is unit-testable without a React render.
 *
 * @param {{currentStep: string, inSession: boolean, armed: boolean, hasBaseline: boolean}} s
 * @return {boolean} true ⇒ run checkDtrStaleness against the in-memory baseline.
 */
const shouldRunInSessionStalenessCheck = ({currentStep, inSession, armed, hasBaseline}) =>
  currentStep === 'summary' && Boolean(inSession) && Boolean(armed) && Boolean(hasBaseline);

/**
 * Selected employees that have an INCOMPLETE time record in the run — the C3 finalize
 * block (PRD_DtrCompletenessGuard). Reads the `emp.hasIncompleteRecords` flag that
 * fetchEmployeesForPayroll derives from the RAW DTR records (via derivePairingStatus)
 * BEFORE the one-sided strip — the strip removes orphans from the day build, so the block
 * can't re-derive from `emp.timeRecords` (they've been filtered out); the flag is the only
 * faithful signal, and it rides on the employee object so it survives draft-resume.
 * De-selecting an employee drops them from the result (the defer path). Pure so the block
 * decision is unit-testable without a React render.
 *
 * @param {Array} employees - payrollRun.employees[] (each with a `hasIncompleteRecords` flag).
 * @param {Set|Array} selectedIds - the run's selected employeeIds.
 * @return {Array} the subset of selected employees flagged incomplete (empty ⇒ allow submit).
 */
const employeesWithIncompleteRecords = (employees, selectedIds) => {
  const isSelected = selectedIds instanceof Set ?
    (id) => selectedIds.has(id) : (id) => (selectedIds || []).includes(id);
  return (employees || []).filter((emp) =>
    emp.hasIncompleteRecords === true && isSelected(emp.employeeId || emp.id));
};

// CL-001 (C10): forward-commit targets held while the resume-time freshness refresh is in
// flight. Unlike STALENESS_BLOCKED_TARGETS this includes 'gross-income': the refresh can
// flip an employee's hasIncompleteRecords / drop their day-build, so even the Step-2→3
// move must wait for it (a single summary callable behind the resume spinner; if it fails the
// flag stays set and the run is held until a reload — PRD_PayrollResumeReprice C2).
const RESUME_REFRESH_BLOCKED_TARGETS = ['gross-income', 'deductions', 'loans', 'adjustments', 'summary', 'submit'];

/**
 * Whether a forward commit to `target` must block because the resume-time freshness
 * refresh (PRD_DtrCompletenessGuard CL-001 C8) is still in flight, or its summary call failed
 * (PRD_PayrollResumeReprice C2 leaves the flag set). A dedicated flag, separate from the
 * in-session check's `stalenessChecking`.
 * @param {boolean} resumeRefreshing - the CL-001 C10 in-flight flag.
 * @param {string} target - the step being advanced to, or 'submit' for the commit path.
 * @return {boolean} true ⇒ block-and-return (toast, don't advance/commit).
 */
const blocksWhileResumeRefreshing = (resumeRefreshing, target) =>
  Boolean(resumeRefreshing) && RESUME_REFRESH_BLOCKED_TARGETS.includes(target);

/**
 * CL-001: pure Submit-path gate on the C3 incomplete-records block — true blocks. Wired
 * into handleProcessPayroll (the modal-open gate) and the Submit button's disable, beside
 * the existing handleSubmitConfirm backstop, so a flag set by the resume refresh on a
 * Summary-step draft blocks the commit even though the Step-2 banner isn't on screen.
 * @param {Array} employees - payrollRun.employees[].
 * @param {Set|Array} selectedIds - the run's selected employeeIds.
 * @return {boolean} true ⇒ block Submit.
 */
const blocksSubmitOnIncomplete = (employees, selectedIds) =>
  employeesWithIncompleteRecords(employees, selectedIds).length > 0;

/**
 * Selected employees with more than one entry on a day in the period (PRD_ShiftRules C4,
 * BL-031). The server's day build keeps one record per date, so payroll stops instead of
 * silently dropping the other. Reads the `emp.splitDayDates` the callable projects; the
 * same shape and defer path as employeesWithIncompleteRecords.
 *
 * @param {Array} employees - payrollRun.employees[] (each with a `splitDayDates` array).
 * @param {Set|Array} selectedIds - the run's selected employeeIds.
 * @return {Array} the subset of selected employees with such a day (empty ⇒ allow).
 */
const employeesWithSplitDays = (employees, selectedIds) => {
  const isSelected = selectedIds instanceof Set ?
    (id) => selectedIds.has(id) : (id) => (selectedIds || []).includes(id);
  return (employees || []).filter((emp) =>
    Array.isArray(emp.splitDayDates) && emp.splitDayDates.length > 0 &&
    isSelected(emp.employeeId || emp.id));
};

// PRD_ShiftRules C5 — the exact copy. The Step-2 pill names the dates; the button reason
// and the Submit backstop toast name the employees.
const splitDayPillText = (dates) =>
  `More than one entry on ${(dates || []).join(', ')} — keep one entry per day`;
const splitDayReasonText = (employees) =>
  `More than one entry on one day for: ${(employees || []).map((e) => e.employeeId || e.id).join(', ')}. ` +
  'Keep one entry per day in Employee Time Records (merge or delete the extra), or de-select the employee to pay them next cycle.';
const splitDayToastText = (employees) => {
  const reason = splitDayReasonText(employees);
  return `Can't submit — ${reason.charAt(0).toLowerCase()}${reason.slice(1)}`;
};

// ============================================================================
// PRD_LoansManagement C1 / C9 / C13 — the wizard's step catalogue, the three
// staleness lists that used to be inline in ProcessPayroll, and the ONE
// client-edit preserve helper shared by Recalculate Gross Income and the
// resume-refresh rebuild.
// ============================================================================

/**
 * The ONE step catalogue (C1). Drives the order, the labels, the progress bar and
 * the stepper. The five staleness/guard lists below are SUBSETS (two carry a
 * non-step 'submit') and LEGACY_STEP_NAME is frozen at six — those stay
 * hand-maintained by design; the render switch in ProcessPayroll is hand-edited too.
 */
const WIZARD_STEPS = [
  {key: 'period-selection', number: 1, label: 'Period Selection'},
  {key: 'employee-selection', number: 2, label: 'Employee Selection'},
  {key: 'gross-income', number: 3, label: 'Gross Income'},
  {key: 'deductions', number: 4, label: 'Deductions'},
  {key: 'loans', number: 5, label: 'Loans'},
  {key: 'adjustments', number: 6, label: 'Adjustments'},
  {key: 'summary', number: 7, label: 'Summary'},
];
const WIZARD_STEP_KEYS = WIZARD_STEPS.map((s) => s.key);

/** 1-based position of a step key (1 for an unknown key, matching the old ternary's fallback). */
const stepNumber = (key) => {
  const i = WIZARD_STEP_KEYS.indexOf(key);
  return i < 0 ? 1 : i + 1;
};

/** Progress-bar width — 14.29 … 100 for seven steps (the inline width is the whole rule). */
const stepProgressWidth = (key) => Math.round((stepNumber(key) / WIZARD_STEPS.length) * 10000) / 100;

const nextStepKey = (key) => {
  const i = WIZARD_STEP_KEYS.indexOf(key);
  return i < 0 || i >= WIZARD_STEP_KEYS.length - 1 ? null : WIZARD_STEP_KEYS[i + 1];
};
const prevStepKey = (key) => {
  const i = WIZARD_STEP_KEYS.indexOf(key);
  return i <= 0 ? null : WIZARD_STEP_KEYS[i - 1];
};

/**
 * C9 — drafts saved BEFORE the seven-step deploy persisted a step NUMBER against
 * the old six-step order. Frozen here on purpose: 5 must resume on Adjustments,
 * never on Loans. Nothing writes a number any more (doSaveDraft and reserveRunId
 * both write the name).
 */
const LEGACY_STEP_NAME = {
  1: 'period-selection',
  2: 'employee-selection',
  3: 'gross-income',
  4: 'deductions',
  5: 'adjustments',
  6: 'summary',
};

/**
 * C9 — resolve a persisted `currentStep` (a name, a legacy number, or nothing) to a
 * step key, keeping the resume read's original fallback.
 */
const resolveDraftStep = (saved) => {
  if (typeof saved === 'string' && WIZARD_STEP_KEYS.includes(saved)) return saved;
  return LEGACY_STEP_NAME[saved] || 'period-selection';
};

// Unresolved DTR staleness (records changed since the save, not yet recalculated):
// handleNext refuses to advance INTO these targets — everything past Gross Income,
// so the runner recalculates before entering a step that prices the stale figure.
const STALE_UNRESOLVED_TARGETS = ['deductions', 'loans', 'adjustments', 'summary'];
// …and the Next button is disabled ON these steps (current-step list, not a target list).
const STALE_UNRESOLVED_NEXT_DISABLED_STEPS = ['employee-selection', 'gross-income', 'deductions', 'loans', 'adjustments'];
// While the background staleness check is in flight, Next is disabled on these steps.
const STALENESS_CHECKING_NEXT_DISABLED_STEPS = ['gross-income', 'deductions', 'loans', 'adjustments'];

const blocksAdvanceOnUnresolvedStaleness = (staleCount, resolved, nextStep) =>
  staleCount > 0 && !resolved && STALE_UNRESOLVED_TARGETS.includes(nextStep);
const disablesNextOnUnresolvedStaleness = (staleCount, resolved, currentStep) =>
  staleCount > 0 && !resolved && STALE_UNRESOLVED_NEXT_DISABLED_STEPS.includes(currentStep);
const disablesNextWhileChecking = (checking, currentStep) =>
  Boolean(checking) && STALENESS_CHECKING_NEXT_DISABLED_STEPS.includes(currentStep);

/**
 * C13 — re-apply the runner's client-side edits over a freshly computed line.
 * Server wins for earnings / timeRecords / late / undertime; the client wins for the
 * four government maps (Deductions), the loans bucket (Loans) and both adjustment arrays
 * (Adjustments). ONE helper for both rebuild paths — Recalculate Gross Income and the
 * resume-refresh merge — so neither can drop a bucket the other keeps (the resume
 * path used to spread the fresh line over the saved one and lose every deduction).
 *
 * @param {Object} saved - the run line as the runner last saw it (may be summary-only).
 * @param {Object} fresh - the fresh server detail (or the saved line spread with it).
 * @return {Object} `fresh` with the client-owned buckets copied from `saved`.
 */
const preserveClientEdits = (saved, fresh) => {
  const s = saved?.deductions || {};
  const govt = {};
  ['sss', 'philhealth', 'pagibig', 'tax'].forEach((k) => {
    if (s[k] !== undefined) govt[k] = s[k];
  });
  return {
    ...fresh,
    earnings: {...(fresh?.earnings || {}), adjustments: saved?.earnings?.adjustments || []},
    deductions: {
      ...(fresh?.deductions || {}),
      ...govt,
      loans: Array.isArray(s.loans) ? s.loans : [],
      adjustments: s.adjustments || [],
    },
  };
};

/**
 * PRD_PayrollResumeReprice C2/C6 — reconcile a resumed draft's saved lines with the live
 * eligibility summary, so a resumed draft is priced exactly like a fresh run.
 *  - A line absent from the summary (date / PII drop, role change) or whose row is
 *    `isEligible === false` is left out of `employees` and reported in `removed` — a fresh
 *    run would not include it, and a line kept in `employees` would carry its saved
 *    `isEligible: true` into Step 2 and stay tickable.
 *  - Every other line becomes the live Step-2 summary row with the runner's edits
 *    re-applied — the same shape a fresh run holds at Step 2. The summary row carries no
 *    `timeRecords`, so the Step-2→3 detail fetch re-prices every line at current settings.
 *
 * @param {Array} employees - the hydrated run lines.
 * @param {Array} liveRows - the eligibility summary's `employees`.
 * @return {{employees: Array, removed: Array<{employeeId: string, reason: string}>}}
 */
const reconcileResumeLines = (employees, liveRows) => {
  const liveById = new Map((liveRows || []).map((r) => [r.employeeId || r.id, r]));
  const kept = [];
  const removed = [];
  (employees || []).forEach((emp) => {
    const employeeId = emp.employeeId || emp.id;
    const live = liveById.get(employeeId);
    if (!live || live.isEligible === false) {
      removed.push({employeeId, reason: live ? (live.exclusionReason || 'Not eligible') : 'not in the eligible list'});
      return;
    }
    kept.push(preserveClientEdits(emp, live));
  });
  return {employees: kept, removed};
};

export {reconcileResumeLines, blocksForwardCommit, makeReentrancyGuard, buildDtrSnapshot, shouldRunInSessionStalenessCheck, employeesWithIncompleteRecords, isCountableDtrRecord, STALENESS_BLOCKED_TARGETS, blocksWhileResumeRefreshing, blocksSubmitOnIncomplete, RESUME_REFRESH_BLOCKED_TARGETS,
  employeesWithSplitDays, splitDayPillText, splitDayReasonText, splitDayToastText,
  WIZARD_STEPS, WIZARD_STEP_KEYS, stepNumber, stepProgressWidth, nextStepKey, prevStepKey, LEGACY_STEP_NAME, resolveDraftStep,
  STALE_UNRESOLVED_TARGETS, STALE_UNRESOLVED_NEXT_DISABLED_STEPS, STALENESS_CHECKING_NEXT_DISABLED_STEPS,
  blocksAdvanceOnUnresolvedStaleness, disablesNextOnUnresolvedStaleness, disablesNextWhileChecking, preserveClientEdits};
